import chalk from 'chalk';

/**
 * Logs to a file.
 */
class FileAppender {
    /**
     * @param stream node.js WriteStream, typed with any to avoid importing node api in browser mode
     */
    constructor(stream, level) {
        this.stream = stream;
        this.type = "file";
        this.level = level;
    }
    /**
     * Write message to file.
     */
    write(message) {
        this.stream.write(`${message}\n`);
    }
    trace(log) {
        this.write(log);
    }
    debug(log) {
        this.write(log);
    }
    info(log) {
        this.write(log);
    }
    warn(log) {
        this.write(log);
    }
    error(log) {
        this.write(log);
    }
}

/**
 * Apollo custom Log levels based on ELK.
 *
 * @see https://wiki.tools.hermes.com/display/APL/Global+logging+using+ELK#GloballoggingusingELK-LogLevels(inorderofseverity)*
 * @see https://datatracker.ietf.org/doc/html/rfc5424
 *
 * Used for ranking levels AND writing the level in the log, so don't change either order or names.
 */
var LogLevel;
(function (LogLevel) {
    /**
     * Application is completely unusable. An alert must be launched and an action done ASAP
     *
     * Example "Connection to database failed"
     */
    LogLevel[LogLevel["Emerg"] = 0] = "Emerg";
    /**
     * Essential part of application is unusable or all application is very unstable.
     * An alert must be launched and an action done ASAP.
     *
     * Example "Prices do not display on product pages"
     */
    LogLevel[LogLevel["Alert"] = 1] = "Alert";
    /**
     * Part of application is unstable or might crash at any time. An alert must be launched and an action done ASAP.
     *
     * Example : Some flows fail
     */
    LogLevel[LogLevel["Crit"] = 2] = "Crit";
    /**
     * Something has gone wrong and must be analysed to avoid business impact.
     *
     * Example : Order could not be created because of the following error : customer does not exist
     */
    LogLevel[LogLevel["Err"] = 3] = "Err";
    /**
     * Non-ideal execution situation. The application works correctly but you must be careful with a minor element.
     *
     * Example : Data X is missing
     */
    LogLevel[LogLevel["Warn"] = 4] = "Warn";
    /**
     * We need to give an information about something special.
     *
     * Example : Import #123456 finished in success
     */
    LogLevel[LogLevel["Notice"] = 5] = "Notice";
    /**
     * Trace key steps.
     *
     * Example : Order #123456 has been created for user #789101112
     */
    LogLevel[LogLevel["Info"] = 6] = "Info";
    /**
     * Debug message for developers.
     */
    LogLevel[LogLevel["Debug"] = 7] = "Debug";
    /**
     * Raw data for developers.
     *
     * Example : Output of customer object
     */
    LogLevel[LogLevel["Trace"] = 8] = "Trace";
})(LogLevel || (LogLevel = {}));

const N_A = "n/a";
/**
 * Allow to format a log message
 *
 * @param message
 * @param level
 */
const format = (level, attributes, message = N_A, ...optionalParameters) => {
    const logAttributes = {
        additionalInformation: stringifyAdditionalParameters(optionalParameters),
        application: attributes.application || N_A,
        applicationVersion: attributes.applicationVersion || N_A,
        cbrArea: attributes.cbrArea || N_A,
        correlationId: attributes.correlationId || N_A,
        customerId: attributes.customerId || N_A,
        environment: attributes.environment || N_A,
        instance: attributes.instance || N_A,
        ip: attributes.ip || N_A,
        level: LogLevel[level],
        lineNumber: attributes.lineNumber || N_A,
        logMessage: message,
        orderId: attributes.orderId || N_A,
        packageClass: attributes.packageClass || N_A,
        productId: attributes.productId || N_A,
        requestId: attributes.requestId || N_A,
        timestamp: createTimeStamp(),
    };
    return inline(logAttributes);
};
/**
 * return a formatted log string, with values in the expected order.
 */
const inline = (data) => [
    data.ip,
    data.timestamp,
    data.cbrArea,
    data.application,
    data.applicationVersion,
    data.environment,
    data.instance,
    data.correlationId,
    data.requestId,
    data.customerId,
    data.orderId,
    data.productId,
    data.level,
    data.packageClass,
    data.lineNumber,
    data.logMessage,
    data.additionalInformation,
]
    .join(" | ")
    // Match \r\n, \n and \r (Global and Multi-line)
    .replace(/(\r\n|\n|\r)/gm, "");
/**
 * @returns an ISO-formatted date
 */
const createTimeStamp = () => new Date().toISOString();
/**
 * JSON stringify additionnalInfos using replacer to remove circular references
 */
const stringifyAdditionalParameters = (additionalInfos) => {
    let response = "";
    if (!additionalInfos || additionalInfos.length === 0) {
        return N_A;
    }
    response = additionalInfos
        .filter((entry) => !!entry)
        .map((entry) => jsonifyPerType(entry))
        .reduce((accumulator, value, index) => {
        accumulator[String(index)] = value;
        return accumulator;
    }, {});
    return JSON.stringify(response);
};
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const jsonifyPerType = (item) => {
    if (item instanceof Error) {
        return jsonifyError(item);
    }
    if (typeof item === "object") {
        return removeCircularDepsFrom(item);
    }
    return item;
};
const removeCircularDepsFrom = (item) => {
    // remove circular dependencies
    const cache = [];
    // clear objects from recursive reference
    const result = JSON.stringify(item, (_key, value) => {
        if (typeof value === "object" && value !== null) {
            if (cache.includes(value)) {
                return;
            }
            cache.push(value);
        }
        return value;
    });
    return JSON.parse(result);
};
const jsonifyError = (item) => ({
    message: item.message,
    stack: item.stack && item.stack.length > 0 && item.stack.split("\n"),
});

/* eslint-disable no-console */ // this is the only file where it is allowed
/**
 * Log to console
 */
class ConsoleAppender {
    constructor(level) {
        this.type = "console";
        this.level = level;
    }
    trace(log, ...parameters) {
        if (parameters.length > 0) {
            console.trace(log, stringifyAdditionalParameters(parameters));
        }
        else {
            console.trace(log);
        }
    }
    debug(log, ...parameters) {
        if (parameters.length > 0) {
            console.debug(log, stringifyAdditionalParameters(parameters));
        }
        else {
            console.debug(log);
        }
    }
    info(log, ...parameters) {
        if (parameters.length > 0) {
            console.info(log, stringifyAdditionalParameters(parameters));
        }
        else {
            console.info(log);
        }
    }
    warn(log, ...parameters) {
        if (parameters.length > 0) {
            console.warn(log, stringifyAdditionalParameters(parameters));
        }
        else {
            console.warn(log);
        }
    }
    error(log, ...parameters) {
        if (parameters.length > 0) {
            console.error(log, stringifyAdditionalParameters(parameters));
        }
        else {
            console.error(log);
        }
    }
}

/* eslint-disable no-console */
/**
 * Logs to a file.
 */
class LocalAppender {
    /**
     * @param stream node.js WriteStream, typed with any to avoid importing node api in browser mode
     */
    constructor(level) {
        this.type = "local";
        this.level = level;
    }
    /**
     * Write message to file.
     */
    trace(log, ...parameters) {
        if (parameters.length > 0) {
            console.trace(chalk.cyan(log), chalk.cyan(stringifyAdditionalParameters(parameters)));
        }
        else {
            console.trace(chalk.cyan(log));
        }
    }
    debug(log, ...parameters) {
        if (parameters.length > 0) {
            console.debug(chalk.blue(log), chalk.blue(stringifyAdditionalParameters(parameters)));
        }
        else {
            console.debug(chalk.blue(log));
        }
    }
    info(log, ...parameters) {
        if (parameters.length > 0) {
            console.info(chalk.green(log), chalk.green(stringifyAdditionalParameters(parameters)));
        }
        else {
            console.info(chalk.green(log));
        }
    }
    warn(log, ...parameters) {
        if (parameters.length > 0) {
            console.warn(chalk.yellow(chalk.bold(log)), chalk.yellow(stringifyAdditionalParameters(parameters)));
        }
        else {
            console.warn(chalk.yellow(chalk.bold(log)));
        }
    }
    error(log, ...parameters) {
        if (parameters.length > 0) {
            console.error(chalk.bold(chalk.red(log)), chalk.red(stringifyAdditionalParameters(parameters)));
        }
        else {
            console.error(chalk.bold(chalk.red(log)));
        }
    }
}

/**
 * This class is a Log middleware.
 *
 * it enable logs in files corresponding to the LogLevel, example :
 * fatal() errors will log with LogLevel.Error level
 *
 */
class AbstractLogger {
    /**
     * @param config: log configuration.
     * @param logFormatter: logFormatter instance.
     */
    constructor(config) {
        /**
         * log outputs.
         */
        this.appenders = [];
        // create appenders.
        Object.keys(config.appenders).forEach((appenderKey) => {
            const { level, type } = config.appenders[appenderKey];
            if (type === "console") {
                this.appenders.push(new ConsoleAppender(level));
            }
            else if (type === "file" &&
                config.appenders["file"].type === "file") {
                this.appenders.push(new FileAppender(config.appenders["file"].stream, level));
            }
            else if (type === "local") {
                this.appenders.push(new LocalAppender(level));
            }
        });
        if (config.level >= 0) {
            this.globalLogLevel = config.level;
        }
        else {
            throw new Error("No log level provided to logger");
        }
        this.logAttributes = {};
    }
    // -- simpler and real functions
    /* eslint-disable @typescript-eslint/explicit-module-boundary-types */
    trace(message, ...optionalParameters) {
        this.write(LogLevel.Trace, message, ...optionalParameters);
    }
    debug(message, ...optionalParameters) {
        this.write(LogLevel.Debug, message, ...optionalParameters);
    }
    notice(message, ...optionalParameters) {
        this.write(LogLevel.Notice, message, ...optionalParameters);
    }
    info(message, ...optionalParameters) {
        this.write(LogLevel.Info, message, ...optionalParameters);
    }
    warn(message, ...optionalParameters) {
        this.write(LogLevel.Warn, message, ...optionalParameters);
    }
    alert(message, ...optionalParameters) {
        this.write(LogLevel.Alert, message, ...optionalParameters);
    }
    error(message, ...optionalParameters) {
        this.write(LogLevel.Err, message, ...optionalParameters);
    }
    critical(message, ...optionalParameters) {
        this.write(LogLevel.Crit, message, ...optionalParameters);
    }
    emergency(message, ...optionalParameters) {
        this.write(LogLevel.Emerg, message, ...optionalParameters);
    }
    /* eslint-enable @typescript-eslint/explicit-module-boundary-types */
    isTraceEnabled() {
        return this.globalLogLevel >= LogLevel.Trace;
    }
    isDebugEnabled() {
        return this.globalLogLevel >= LogLevel.Debug;
    }
    isInfoEnabled() {
        return this.globalLogLevel >= LogLevel.Info;
    }
    isWarnEnabled() {
        return this.globalLogLevel >= LogLevel.Warn;
    }
    isAlertEnabled() {
        return this.globalLogLevel >= LogLevel.Alert;
    }
    isErrorEnabled() {
        return this.globalLogLevel >= LogLevel.Err;
    }
    isCriticalEnabled() {
        return this.globalLogLevel >= LogLevel.Crit;
    }
    isEmergencyEnabled() {
        return this.globalLogLevel >= LogLevel.Emerg;
    }
    /**
     * Change current log level.
     */
    setLevel(logLevel) {
        this.globalLogLevel = logLevel;
    }
    setLogAttributes(application, applicationVersion, environment, instance, lineNumber, packageClass, requestId, correlationId, ip, cbrArea, customerId, orderId, productId) {
        this.logAttributes.application = application;
        this.logAttributes.applicationVersion = applicationVersion;
        this.logAttributes.cbrArea = cbrArea;
        this.logAttributes.correlationId = correlationId;
        this.logAttributes.customerId = customerId;
        this.logAttributes.environment = environment;
        this.logAttributes.instance = instance;
        this.logAttributes.ip = ip;
        this.logAttributes.lineNumber = lineNumber;
        this.logAttributes.orderId = orderId;
        this.logAttributes.packageClass = packageClass;
        this.logAttributes.productId = productId;
        this.logAttributes.requestId = requestId;
    }
    setAppLogAttributes(application, applicationVersion, environment, instance) {
        this.logAttributes.application = application;
        this.logAttributes.applicationVersion = applicationVersion;
        this.logAttributes.environment = environment;
        this.logAttributes.instance = instance;
    }
    setRequestLogAttributes(requestId, correlationId, ip, cbrArea, customerId, orderId, productId) {
        this.logAttributes.cbrArea = cbrArea;
        this.logAttributes.correlationId = correlationId;
        this.logAttributes.customerId = customerId;
        this.logAttributes.ip = ip;
        this.logAttributes.orderId = orderId;
        this.logAttributes.productId = productId;
        this.logAttributes.requestId = requestId;
    }
    setCodeLogAttributes(lineNumber, packageClass) {
        this.logAttributes.lineNumber = lineNumber;
        this.logAttributes.packageClass = packageClass;
    }
    /**
     * 1. Formats the message with LogFormatter's generateLog function.
     * 2. Logs it with log4js.
     *
     * @param message message to be formatted and logged
     * @param level log level of the message
     */
    write(level, message, ...optionalParameters) {
        const isLevelLoggableBySomeAppenders = this.appenders.some((appender) => 
        // level is configured for appender if configured or global level
        (appender.level !== undefined && level <= appender.level) ||
            level <= this.globalLogLevel);
        if (!isLevelLoggableBySomeAppenders) {
            return;
        }
        // if the params contains an error object, extract stack trace.
        message =
            message instanceof Error
                ? this.appendErrorStack("", [message])
                : this.appendErrorStack(message, optionalParameters);
        // generate log message
        message = format(level, this.logAttributes, message);
        // send to appenders
        this.appenders.forEach((appender) => {
            // level is configured for appender => ignore global level
            const isAppenderLevelLoggable = appender.level !== undefined && level <= appender.level;
            // no level configured for appender => check global level
            const isGlobalLevelLoggable = appender.level === undefined && level <= this.globalLogLevel;
            if (isAppenderLevelLoggable || isGlobalLevelLoggable) {
                switch (level) {
                    // these four levels logs in error(log4js) but still has right level for elk.
                    case LogLevel.Emerg:
                    case LogLevel.Alert:
                    case LogLevel.Crit:
                    case LogLevel.Err:
                        appender.error(message, ...optionalParameters);
                        break;
                    case LogLevel.Warn:
                        appender.warn(message, ...optionalParameters);
                        break;
                    // these two levels log in info level at log4js
                    case LogLevel.Notice:
                    case LogLevel.Info:
                        // logs with log levels info and notice should be logged as info
                        appender.info(message, ...optionalParameters);
                        break;
                    case LogLevel.Debug:
                        appender.debug(message, ...optionalParameters);
                        break;
                    case LogLevel.Trace:
                        appender.trace(message, ...optionalParameters);
                        break;
                }
            }
        });
    }
    appendErrorStack(message, parameters) {
        parameters
            .filter((parameter) => (parameter === null || parameter === void 0 ? void 0 : parameter.message) && (parameter === null || parameter === void 0 ? void 0 : parameter.stack))
            .forEach((error) => {
            message += ` ${error.message} ${error.stack}`;
        });
        return message;
    }
}

/**
 * A very simple implementation for node.js logging.
 */
class ServerLogger extends AbstractLogger {
    constructor(logConfig) {
        super(logConfig);
    }
}

export { AbstractLogger, LogLevel, ServerLogger };
